import { useState } from 'react';
import { SIM_CONSENT_STATUS } from '../../hooks/useSimConsent';
import styles from './SimConsentStatus.module.css';
import useViewOnlyAccess from '../../../../hooks/useViewOnlyAccess';

/**
 * Reusable component for displaying SIM-based tracking consent status
 * @param {object} props
 * @param {string|null} props.consentStatus - Current consent status (PENDING, ALLOWED, REJECTED, or null)
 * @param {boolean} props.loading - Whether consent details are loading
 * @param {boolean} props.isSendingRequest - Whether a consent request is being sent
 * @param {boolean} props.isCheckingStatus - Whether consent status is being checked
 * @param {function} props.onSendRequest - Callback when send request button is clicked
 * @param {function} props.onCheckStatus - Callback when check status button is clicked
 * @param {boolean} props.showInfoBox - Whether to show the information box (default: true)
 * @param {object} props.customStyles - Optional custom styles object to override defaults
 */
const SimConsentStatus = ({
  consentStatus,
  restrictSimTracking = false,
  loading = false,
  isSendingRequest = false,
  isCheckingStatus = false,
  onSendRequest,
  onCheckStatus,
  showInfoBox = true,
  customStyles = {},
}) => {
  const [isInfoExpanded, setIsInfoExpanded] = useState(false);
  const { isRestricted: viewOnly, handleRestrictedClick } = useViewOnlyAccess('indent', 'lorri_indent_tracking');

  // Merge custom styles with default styles
  const mergedStyles = { ...styles, ...customStyles };

  if (loading) {
    return (
      <div className={mergedStyles.consentContainer}>
        <div className="d-flex justify-content-center align-items-center" style={{ padding: '2rem' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading consent details...</span>
          </div>
          <span style={{ marginLeft: '1rem', color: '#6c757d' }}>Loading consent details...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* No Consent Status - Initial State */}
      {consentStatus === null && (
        <div className={mergedStyles.consentContainer}>
          {
            !restrictSimTracking && <div className={mergedStyles.consentStatus}>
              <i className="fa fa-info-circle" style={{ color: '#6c757d', marginRight: '8px' }}></i>
              <span className={mergedStyles.statusText}>No consent request sent yet</span>
            </div>
          }
          <div className={mergedStyles.consentDescription}>
            {
              restrictSimTracking ? "This vehicle has already reached its destination" :
                "Send a consent request to the driver to enable SIM-based vehicle tracking."
            }
          </div>
          <button
            className={mergedStyles.primaryButton}
            onClick={() => (viewOnly ? handleRestrictedClick() : onSendRequest())}
            disabled={isSendingRequest || restrictSimTracking}
            style={viewOnly ? { opacity: 0.7, cursor: 'not-allowed' } : {}}
          >
            {isSendingRequest ? (
              <>
                <div className="spinner-border mr-3" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
                Sending...
              </>
            ) : (
              <>
                <i className="fa fa-paper-plane" style={{ marginRight: '8px' }}></i>
                Send Consent Request
              </>
            )}
          </button>
        </div>
      )}

      {/* Pending Status */}
      {consentStatus === SIM_CONSENT_STATUS.PENDING && (
        <div className={mergedStyles.consentContainer}>
          <div className={mergedStyles.consentStatus}>
            <i className="fa fa-clock-o" style={{ color: '#f39c12', marginRight: '8px' }}></i>
            <span className={mergedStyles.statusText} style={{ color: '#f39c12' }}>
              Consent request pending
            </span>
          </div>
          <div className={mergedStyles.consentDescription}>
            Waiting for driver to approve the SIM tracking consent request.
          </div>
          <button
            className={mergedStyles.secondaryButton}
            onClick={onCheckStatus}
            disabled={isCheckingStatus}
          >
            {isCheckingStatus ? (
              <>
                <i className="fa fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>
                Checking...
              </>
            ) : (
              <>
                <i className="fa fa-refresh" style={{ marginRight: '8px' }}></i>
                Check Status Update
              </>
            )}
          </button>
        </div>
      )}

      {/* Approved Status */}
      {consentStatus === SIM_CONSENT_STATUS.ALLOWED && (
        <div className={mergedStyles.consentContainer}>
          <div className={mergedStyles.consentStatus}>
            <i className="fa fa-check-circle" style={{ color: '#27ae60', marginRight: '8px' }}></i>
            <span className={mergedStyles.statusText} style={{ color: '#27ae60' }}>
              Consent approved
            </span>
          </div>
          <div className={mergedStyles.consentDescription}>
            Driver has approved SIM-based tracking. Vehicle tracking is now active.
          </div>
          <div className={mergedStyles.successBadge}>
            <i className="fa fa-shield" style={{ marginRight: '8px' }}></i>
            Tracking Active
          </div>
        </div>
      )}

      {/* Rejected Status */}
      {consentStatus === SIM_CONSENT_STATUS.REJECTED && (
        <div className={mergedStyles.consentContainer}>
          <div className={mergedStyles.consentStatus}>
            <i className="fa fa-times-circle" style={{ color: '#e74c3c', marginRight: '8px' }}></i>
            <span className={mergedStyles.statusText} style={{ color: '#e74c3c' }}>
              Consent rejected
            </span>
          </div>
          <div className={mergedStyles.consentDescription}>
            Driver has rejected the SIM tracking consent. You can resend the request or contact the driver directly.
          </div>
          <div className={mergedStyles.buttonGroup}>
            <button
              className={mergedStyles.primaryButton}
              onClick={() => (viewOnly ? handleRestrictedClick() : onSendRequest())}
              disabled={isSendingRequest}
              style={viewOnly ? { opacity: 0.7, cursor: 'not-allowed' } : {}}
            >
              {isSendingRequest ? (
                <>
                  <i className="fa fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>
                  Sending...
                </>
              ) : (
                <>
                  <i className="fa fa-repeat" style={{ marginRight: '8px' }}></i>
                  Resend Request
                </>
              )}
            </button>
            <button
              className={mergedStyles.secondaryButton}
              onClick={() => alert('Contact driver feature - to be implemented')}
            >
              <i className="fa fa-phone" style={{ marginRight: '8px' }}></i>
              Contact Driver
            </button>
          </div>
        </div>
      )}

      {/* Collapsible Information Box */}
      {showInfoBox && (
        <div className={mergedStyles.infoBox}>
          <div
            className={mergedStyles.infoHeader}
            onClick={() => setIsInfoExpanded(!isInfoExpanded)}
            style={{ cursor: 'pointer' }}
          >
            <i className="fa fa-info-circle" style={{ marginRight: '8px' }}></i>
            <strong>About SIM-Based Vehicle Tracking</strong>
            <i
              className={`fa fa-chevron-${isInfoExpanded ? 'up' : 'down'}`}
              style={{ marginLeft: 'auto', fontSize: '0.85rem' }}
            ></i>
          </div>
          {isInfoExpanded && (
            <div className={mergedStyles.infoContent}>
              <div className={mergedStyles.infoText}>
                <i className="fa fa-shield" style={{ marginRight: '6px', color: '#3498db' }}></i>
                <strong>TRAI Compliance:</strong> Driver consent is mandatory as per TRAI (Telecom Regulatory Authority of India) regulations for tracking via driver's SIM card.
              </div>
              <div className={mergedStyles.infoText}>
                <i className="fa fa-location-arrow" style={{ marginRight: '6px', color: '#3498db' }}></i>
                <strong>How it works:</strong> Vehicle location is tracked using the driver's mobile SIM card network data.
              </div>
              <div className={mergedStyles.infoText}>
                <i className="fa fa-clock-o" style={{ marginRight: '6px', color: '#3498db' }}></i>
                <strong>Tracking Duration:</strong> Tracking begins when the vehicle is dispatched and ends when vehicle is reached.
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default SimConsentStatus;
